import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatErr(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const em = email.trim().toLowerCase();
    if (!em || !EMAIL_RE.test(em)) return setErr("Please enter a valid email address.");
    if (!password) return setErr("Please enter your password.");

    setBusy(true);
    try {
      const u = await login(em, password);
      toast.success("Welcome back!");
      const isStaff = (u.roles || []).some(r => ["admin", "dispatcher", "driver"].includes(r));
      nav(isStaff ? "/dashboard" : "/portal-select");
    } catch (e) {
      const detail = formatErr(e?.response?.data?.detail);
      const status = e?.response?.status;
      // Friendlier message for the most common failure
      if (/invalid email or password/i.test(detail) || status === 401) {
        setErr("Email or password is incorrect. Try again, or reset your password.");
      } else if (!e?.response) {
        setErr("Can't reach the server. Check your internet connection and try again.");
      } else {
        setErr(detail || `Login failed (HTTP ${status}). Please try again or contact support.`);
      }
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal-select";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="logo" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-heading font-black text-lg">CLEAVIE'S ENTERPRISE</div>
            <div className="text-xs tracking-[0.22em] text-[#FFCE00]">ANTIGUA & BARBUDA</div>
          </div>
        </Link>
        <div className="relative z-10">
          <h2 className="font-heading font-black text-4xl">Welcome back to the crew.</h2>
          <p className="mt-3 text-slate-300 max-w-md">
            Sign in to track your bookings, manage jobs, and keep Antigua moving.
          </p>
        </div>
        <div className="text-xs text-slate-500">© {new Date().getFullYear()} Cleavie's Enterprise</div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-[#E32636]/30 rounded-full blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={submit} noValidate className="w-full max-w-sm space-y-5">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
            <div className="font-heading font-black text-slate-900">CLEAVIE'S ENTERPRISE</div>
          </div>
          <div>
            <div className="tag-overline">Sign in</div>
            <h1 className="font-heading font-black text-3xl text-slate-900 mt-1">Welcome back</h1>
          </div>

          <Button type="button" onClick={googleLogin} variant="outline" data-testid="google-login-btn"
                  className="w-full h-11 border-2 border-slate-200 hover:border-slate-400 font-bold">
            <span className="mr-2">G</span> Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px bg-slate-200 flex-1" /> OR <div className="h-px bg-slate-200 flex-1" />
          </div>

          {err && (
            <div data-testid="login-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>{err}</div>
            </div>
          )}

          <div>
            <Label className="text-sm font-bold text-slate-900">Email</Label>
            <Input type="email" inputMode="email" autoComplete="email" data-testid="login-email"
                   value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div>
            <Label className="text-sm font-bold text-slate-900">Password</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} data-testid="login-password"
                     autoComplete="current-password"
                     value={password} onChange={e => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPw(s => !s)} data-testid="login-pw-toggle"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 p-1">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit-btn"
                  className="w-full h-11 bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-center">
            <Link to="/forgot-password" className="text-sm font-bold text-[#1D4ED8] hover:underline"
                  data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <p className="text-sm text-slate-600 text-center">
            New here? <Link to="/register" className="font-bold text-[#1D4ED8] hover:underline">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
